<template>
  <BaseHeadline :level="1">
    <BaseColor color="muted">
      Plans:
    </BaseColor>
    {{ FILTERS.find(filter => filter.value === activeFilter)?.label }}
  </BaseHeadline>

  <FlexLayout
    v-if="plansStore.isLoading"
    align="center"
    gap="xs"
  >
    <BaseIcon
      id="spinner"
      inline
    />
    <BaseText flush>
      {{ plansStore.loadingDescription }}
    </BaseText>
  </FlexLayout>

  <div
    :aria-busy="plansStore.isLoading"
    class="plans-table"
    :class="{ 'is-refetching': plansStore.isLoading }"
    :inert="plansStore.isLoading || undefined"
  >
    <AppDataTable
      :columns="columns"
      :error="plansStore.error"
      :fixed-columns="true"
      :is-loading="plansStore.isLoading && plansStore.plans.length === 0"
      :row-clickable="true"
      :row-expandable="true"
      row-key="paymentPlanId"
      :rows="plansStore.plans"
      :show-column-toggles="true"
      :sort-by="sortBy"
      :sort-order="sortOrder"
      :storage-key="STORAGE_KEY"
      :visible-columns="[
        'toggle',
        'customerName',
        'bookingReference',
        'kind',
        'paymentPlanId',
        'depositAmount',
        'nextInstalmentDate',
        'totalAmount',
        'status',
        'actions',
      ]"
      @row-click="viewPlanDetails"
    >
      <template #status="{ row }">
        <FlexLayout
          align="center"
          flush
          gap="xs"
          justify="end"
        >
          <PlanStatusBadge
            v-if="row.paymentMethodStatus === 'halted'"
            align="right"
            status="paused"
            variant="solid"
          />
          <PlanStatusBadge
            align="right"
            :status="row.status"
            variant="solid"
          />
        </FlexLayout>
      </template>

      <template #workflowId="{ row }">
        <CopyToClipboard :value="row.workflowId" />
        {{ row.workflowId }}
      </template>

      <template #assetId="{ row }">
        <CopyToClipboard :value="row.assetId" />
        {{ row.assetId }}
      </template>

      <template #paymentPlanId="{ row }">
        <CopyToClipboard :value="row.paymentPlanId" />
        {{ row.paymentPlanId }}
      </template>

      <template #bookingReference="{ row }">
        <CopyToClipboard :value="row.bookingReference" />
        {{ row.bookingReference }}
      </template>

      <template #kind="{ row }">
        {{ formatPlanKind(row.kind) }}
      </template>

      <template #depositDate="{ row }">
        <template v-if="planPresentation(row.kind).dashboard.checkoutDateLabel">
          {{ planPresentation(row.kind).dashboard.checkoutDateLabel }} <strong>{{ row.depositDate }}</strong>
        </template>
        <template v-else>
          {{ row.depositDate }}
        </template>
      </template>

      <template #depositAmount="{ row }">
        <template v-if="planPresentation(row.kind).dashboard.checkoutAmountLabel">
          {{ planPresentation(row.kind).dashboard.checkoutAmountLabel }} <strong>{{ row.depositAmount }}</strong>
        </template>
        <template v-else>
          {{ row.depositAmount }}
        </template>
      </template>

      <template #nextInstalmentDate="{ row }">
        {{ planPresentation(row.kind).dashboard.nextInstalmentPlaceholder ?? row.nextInstalmentDate }}
      </template>

      <template #actions>
        <BaseIcon
          id="caret-right"
          size="sm"
        />
      </template>

      <template #expanded-content="{ row }">
        <FlexLayout
          v-for="column in expandedColumnsFor(row.kind)"
          :key="column.key"
        >
          <FlexItem :span="3">
            {{ column.label }}:
          </FlexItem>
          <FlexItem :span="3">
            <strong>{{ row[column.key] }}</strong>
          </FlexItem>
        </FlexLayout>
      </template>

      <template #empty>
        <FlexLayout
          v-if="plansStore.hasMore"
          direction="column"
          gap="md"
        >
          <FlexItem>
            <BaseIcon
              id="info"
              inline
            />
            No viewable plans in this batch. Load more to check the next batch.
          </FlexItem>
        </FlexLayout>
        <FlexLayout
          v-else
          direction="column"
          gap="md"
        >
          <FlexItem>
            <BaseIcon
              id="info"
              inline
            />
            No results found for:
            <strong>{{ searchValue }}</strong>
          </FlexItem>
          <FlexItem>
            Booking not showing up? Enter the booking reference (PLT) or the payment plan ID (SlicePay) below and we'll
            do a deeper search:
          </FlexItem>
          <FlexItem>
            <BaseGroup>
              <FormControl
                v-model.trim="search"
                flush
                placeholder="eg PLT21337 or plan_21337..."
                type="text"
                @keyup.enter="handleExtendedSearch"
              />
              <BaseButton
                color="primary"
                :disabled="!search || isSearching"
                icon="binoculars"
                :loading="isSearching"
                :variant="search ? 'solid' : 'outline'"
                @click="handleExtendedSearch"
              >
                Search
              </BaseButton>
            </BaseGroup>
          </FlexItem>
          <AlertMessage
            v-if="searchError"
            :message="searchError"
            title="Search Failed"
            type="error"
          />
        </FlexLayout>
      </template>
    </AppDataTable>
  </div>

  <AlertMessage
    v-if="plansStore.lastAppendWasEmpty"
    message="No additional viewable plans in that batch. Load more to keep looking."
    type="info"
  />

  <AppLoadMoreControl v-if="plansStore.hasMoreToLoad" />
</template>

<script setup lang="ts">
import AppDataTable from '@components/common/AppDataTable.vue';
import AppLoadMoreControl from '@components/common/AppLoadMoreControl.vue';
import PlanStatusBadge from '@components/plans/PlanStatusBadge.vue';
import { PLANS_QUERY_DEFAULTS, STORAGE_KEY } from '@constants/plansQuery';
import type { TableColumn } from '@plt/ui-vue';
import { useToast } from '@plt/ui-vue';
import { useRoute, useRouter } from 'vue-router';

import type { PaymentPlanKind } from '@/api/response-types';
import { FILTERS } from '@/constants/filters';
import { api } from '@/services/api';
import { usePlansStore } from '@/stores/plans';
import { usePlansQueryStore } from '@/stores/plansQuery';
import { formatPlanKind, planPresentation } from '@/utils/planPresentation';

const router = useRouter();
const route = useRoute();
const plansQueryStore = usePlansQueryStore();
const plansStore = usePlansStore();
const { showToast } = useToast();
const plansLimit = ref(PLANS_QUERY_DEFAULTS.LIMIT);
const search = ref('');
const isSearching = ref(false);
const searchError = ref<string | null>(null);

const sortBy = computed(() => plansQueryStore.sortBy);
const sortOrder = computed(() => plansQueryStore.sortOrder);
const searchValue = computed(() => plansQueryStore.search);

onMounted(async () => {
  plansQueryStore.syncFromRoute(route);
  const paramsUpdated = await plansQueryStore.ensureDefaultSortParams(router, route);

  if (!paramsUpdated) {
    plansStore.fetchPlans(activeFilter.value, {
      ...plansQueryStore.apiQueryParams,
      limit: plansLimit.value,
    });
  }
});

const mainColumns = ref<TableColumn[]>([
  {
    key: 'toggle',
    label: '',
    scope: 'col',
    width: '3rem',
  },
  {
    key: 'customerName',
    label: 'Customer',
    sortable: true,
    sortKey: 'customer_name',
  },
  {
    key: 'paymentPlanId',
    label: 'Payment plan ID',
  },
  {
    key: 'workflowId',
    label: 'Workflow ID',
  },
  {
    key: 'assetId',
    label: 'Asset ID',
  },
  {
    key: 'bookingReference',
    label: 'Reference',
    sortable: true,
    sortKey: 'booking_reference',
  },
  {
    key: 'kind',
    label: 'Kind',
  },
  {
    key: 'depositDate',
    label: 'Deposit date',
    sortable: true,
    sortKey: 'deposit_date',
    align: 'center',
  },
  {
    key: 'depositAmount',
    label: 'Deposit amount',
    align: 'center',
  },
  {
    key: 'departureDate',
    label: 'Departure',
    sortable: true,
    sortKey: 'departure_date',
    align: 'center',
  },
  {
    key: 'nextInstalmentDate',
    label: 'Next instalment',
    align: 'center',
  },
  {
    key: 'outstandingAmount',
    label: 'Outstanding',
    align: 'center',
  },
  {
    key: 'totalAmount',
    label: 'Total',
    sortable: true,
    sortKey: 'total_amount',
    align: 'center',
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    sortKey: 'status',
    align: 'right',
  },
  {
    key: 'actions',
    label: '',
    scope: 'col',
    width: '3rem',
  },
]);

const expandedPlanData = ref<TableColumn[]>([
  {
    key: 'customerEmail',
    label: 'Customer email',
  },
  {
    key: 'numberOfOverdueInstalments',
    label: 'Overdue instalments',
  },
  {
    key: 'numberOfInstalments',
    label: 'Total instalments',
  },
  {
    key: 'numberOfOutstandingInstalments',
    label: 'Outstanding instalments',
  },
  {
    key: 'paymentPlanId',
    label: 'Payment plan ID',
  },
]);

const instalmentCountColumnKeys = [
  'numberOfOverdueInstalments',
  'numberOfInstalments',
  'numberOfOutstandingInstalments',
];

const expandedColumnsFor = (kind: PaymentPlanKind) =>
  planPresentation(kind).showSchedule
    ? expandedPlanData.value
    : expandedPlanData.value.filter((column) => !instalmentCountColumnKeys.includes(column.key));

const columns = computed(() => mainColumns.value);

const activeFilter = computed(() => plansQueryStore.filter);

const viewPlanDetails = (plan: Record<string, unknown>, openInNewTab = false) => {
  const url = `/plan/${plan.paymentPlanId}`;
  if (openInNewTab) {
    window.open(url, '_blank');
  } else {
    router.push(url);
  }
};

const handleExtendedSearch = async () => {
  if (!search.value) return;

  isSearching.value = true;
  searchError.value = null;

  try {
    const response = await api.searchUnavailablePlan(search.value);

    if (response.success && response.paymentPlanId) {
      showToast({
        type: 'success',
        title: 'Plan found!',
        message: 'Redirecting to plan details...',
      });
      router.push(`/plan/${response.paymentPlanId}`);
    } else {
      searchError.value = 'Plan not found. The booking reference may not exist or may not have a payment plan.';
    }
  } catch (err) {
    console.error('Extended search error:', err);
    searchError.value = err instanceof Error ? err.message : 'An error occurred while searching. Please try again.';
  } finally {
    isSearching.value = false;
  }
};

watch(search, () => {
  if (searchError.value) {
    searchError.value = null;
  }
});

watch(
  () => [route.query.filter, route.query.sort_by, route.query.sort_order, route.query.search],
  async () => {
    plansQueryStore.syncFromRoute(route);
    const paramsUpdated = await plansQueryStore.ensureDefaultSortParams(router, route);

    if (!paramsUpdated) {
      plansStore.fetchPlans(activeFilter.value, {
        ...plansQueryStore.apiQueryParams,
        limit: plansLimit.value,
      });
    }
  },
);
</script>

<style scoped>
.plans-table {
  --plans-table-refetch-opacity: 0.6;

  transition: var(--transition-default);
}

.plans-table.is-refetching {
  opacity: var(--plans-table-refetch-opacity);
  pointer-events: none;
}

:deep(.empty-cell) {
  background-color: var(--color-neutral-100);
}
</style>
